import { z } from 'zod'

import {
  bytesSchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  limitSchema,
  moneyPaisa,
  noteSchema,
  planCodeSchema,
  roleSchema,
  searchQuerySchema,
  slugSchema,
  sortSchema,
  subscriptionStatusSchema,
  tenantStatusSchema,
  usernameSchema,
} from '../common'
import { passwordSchema } from '../auth/auth'
import { cursorQuerySchema } from '../pagination'

/**
 * P2–P4, P8 · Businesses.
 *
 * `bookValuePaisa` is the one figure here that comes out of a trader's ledger,
 * and it is a **total cost basis**, never a list of animals or a profit. It is
 * also money, so it obeys the same optionality rule — a support actor who may
 * not see revenue simply receives a row without it.
 */
export const businessSummarySchema = z.object({
  id: idSchema,
  slug: slugSchema,
  name: z.string(),
  city: z.string().nullish(),
  ownerName: z.string(),
  ownerPhone: z.string().nullish(),
  planCode: planCodeSchema,
  status: tenantStatusSchema,
  /** Head of cattle. A count — the platform never enumerates a herd. */
  animals: z.number().int().nonnegative(),
  users: z.number().int().nonnegative(),
  bookValuePaisa: moneyPaisa(),
  storageBytes: bytesSchema,
  /** What they pay a month. Absent on a trial — nothing is being charged. */
  paysPaisa: moneyPaisa(),
  since: isoDateSchema,
  trialEndsOn: isoDateSchema.nullish(),
  lastActivityAt: isoInstantSchema.nullish(),
})

export const BUSINESS_SORTS = [
  'name',
  '-since',
  '-animals',
  '-storage',
  '-bookValue',
  '-pays',
] as const

export const businessListQuery = cursorQuerySchema.extend({
  status: tenantStatusSchema.optional(),
  planCode: planCodeSchema.optional(),
  q: searchQuerySchema.optional(),
  /** Rows at or above 80% of any quota — the "needs a bigger plan" filter. */
  nearLimit: z.coerce.boolean().optional(),
  sort: sortSchema(BUSINESS_SORTS, 'name'),
})

export const businessListResponse = z.array(businessSummarySchema)

/* ── P3 · POST /platform/businesses ───────────────────────────────────────── */

/**
 * The console creating a book on someone's behalf — a trader signed up over the
 * phone. Same transaction as A3: tenant, owner, subscription, feed plan and
 * settings, or nothing.
 */
export const createBusinessRequest = z.object({
  name: z.string().trim().min(2).max(120),
  city: z.string().trim().max(80).optional(),
  ownerName: z.string().trim().min(2).max(120),
  ownerUsername: usernameSchema,
  ownerPhone: z.string().trim().max(30).optional(),
  /** Omitted, the server generates one and returns it once (like O2). */
  ownerPassword: passwordSchema.optional(),
  planCode: planCodeSchema,
  trialDays: z.number().int().min(0).max(365).optional(),
  note: noteSchema.optional(),
})

export const createBusinessResponse = z.object({
  business: businessSummarySchema,
  oneTimePassword: z.string().min(8).optional(),
})

/* ── P4 · GET /platform/businesses/:id ────────────────────────────────────── */

export const businessUsageSchema = z.object({
  animals: z.number().int().nonnegative(),
  animalLimit: limitSchema,
  users: z.number().int().nonnegative(),
  userLimit: limitSchema,
  storageBytes: bytesSchema,
  storageLimitBytes: limitSchema,
  mediaCount: z.number().int().nonnegative(),
  computedAt: isoInstantSchema,
})

export const businessSubscriptionSchema = z.object({
  id: idSchema,
  planCode: planCodeSchema,
  status: subscriptionStatusSchema,
  currentPeriodStart: isoDateSchema,
  currentPeriodEnd: isoDateSchema,
  nextChargeOn: isoDateSchema.nullish(),
  cancelAtPeriodEnd: z.boolean(),
  amountPaisa: moneyPaisa(),
  paymentMethodMasked: z.string().nullish(),
})

export const businessHealthSchema = z.object({
  lastBackupAt: isoInstantSchema.nullish(),
  failedBackups7d: z.number().int().nonnegative(),
  videoQueue: z.number().int().nonnegative(),
  failedMedia24h: z.number().int().nonnegative(),
  lastActivityAt: isoInstantSchema.nullish(),
})

export const businessDetailResponse = z.object({
  business: businessSummarySchema,
  usage: businessUsageSchema,
  subscription: businessSubscriptionSchema.nullish(),
  people: z.array(
    z.object({
      id: idSchema,
      name: z.string(),
      username: usernameSchema,
      role: roleSchema,
      isActive: z.boolean(),
      lastSignedInAt: isoInstantSchema.nullish(),
    }),
  ),
  health: businessHealthSchema,
  openTicketCount: z.number().int().nonnegative(),
})

/* ── P8 · the danger zone (PLATFORM_OWNER only) ───────────────────────────── */

export const extendTrialRequest = z.object({
  days: z.number().int().min(1).max(120),
  reason: z.string().trim().min(1).max(500),
})

/**
 * Both `/suspend` and `/read-only` take exactly this, and since C10 was decided
 * on 10 September 2026 they do the same thing to the book: it stays readable,
 * every write is refused, and its people still sign in. `SUSPENDED` records
 * that a human froze it and only a human lifts it; `READ_ONLY` is where dunning
 * escalates to and thaws from when a payment lands. These are seasonal
 * businesses — a mid-season lockout loses the customer permanently, so there
 * is no longer a route that performs one.
 */
export const changeTenantStatusRequest = z.object({
  reason: z.string().trim().min(1).max(500),
})

/** Deleting a book asks for its slug typed back. Nothing else undoes it. */
export const deleteBusinessRequest = z.object({
  reason: z.string().trim().min(1).max(500),
  confirmSlug: slugSchema,
})

export const businessStatusResponse = z.object({
  id: idSchema,
  status: tenantStatusSchema,
  trialEndsOn: isoDateSchema.nullish(),
  dataRetentionUntil: isoDateSchema.nullish(),
})

export type BusinessSummary = z.infer<typeof businessSummarySchema>
export type BusinessListQuery = z.input<typeof businessListQuery>
/**
 * P14 · notify ONE owner — the thing `broadcastRequest` structurally cannot do.
 *
 * Screen 05 (§6.1 P-08) has a "Notify owner" action beside a book that needs
 * attention: a backup that has not run, a video queue that is stuck. The only
 * messaging route that existed was the broadcast, and its audience is a FILTER
 * — `status`, `planCode`, `nearLimit` — with no way to name a tenant. Sending
 * one man a message by choosing a filter that happens to match only him is a
 * message to an audience nobody chose, so the control was removed rather than
 * faked. This is that control's route.
 *
 * `role: 'both'`, unlike the broadcast's owner-only: telling one trader his
 * backups are failing is exactly what a support account exists to do.
 *
 * G22 is still open, so nothing is delivered — see `notifyOwnerResponse`.
 */
export const notifyOwnerRequest = z.object({
  channel: z.enum(['WHATSAPP', 'SMS', 'EMAIL', 'IN_APP']),
  subject: z.string().trim().min(3).max(200),
  body: z.string().trim().min(3).max(5000),
  /** Why this owner was contacted. Written to the access log with the message. */
  reason: z.string().trim().min(3).max(500),
  note: noteSchema.optional(),
})

/**
 * A receipt, not a result — the same distinction `broadcastResponse` draws.
 *
 * `ownerName` is echoed so the console can say who it was addressed to without
 * a second request, and `delivered` is stated rather than implied: while G22
 * has no provider it is always `false`, and a screen that reads this field
 * cannot accidentally tell a support engineer the message went out.
 */
export const notifyOwnerResponse = z.object({
  notificationId: idSchema,
  ownerName: z.string(),
  delivered: z.boolean(),
  /** Absent once a provider exists; today it says why nothing was sent. */
  undeliveredReason: z.string().optional(),
  status: z.enum(['QUEUED']),
})

export type NotifyOwnerRequest = z.input<typeof notifyOwnerRequest>
export type NotifyOwnerResponse = z.infer<typeof notifyOwnerResponse>

export type CreateBusinessRequest = z.input<typeof createBusinessRequest>
export type BusinessDetailResponse = z.infer<typeof businessDetailResponse>
export type ExtendTrialRequest = z.input<typeof extendTrialRequest>
export type ChangeTenantStatusRequest = z.input<typeof changeTenantStatusRequest>
export type DeleteBusinessRequest = z.input<typeof deleteBusinessRequest>
